import { useAuthContext, usePermissions } from '/src/hooks';
import { UpsellWall } from '/src/components/UpsellWall';
import { WebP } from '/src/components/WebP';
import { FEATURES } from '/src/features/_global/constants';

import FeaturedImagePNG from '@crazyegginc/hatch/dist/images/illustration-funnels.png';
import FeaturedImageWebP from '@crazyegginc/hatch/dist/images/illustration-funnels.webp';
import LeftLogoWebP from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.webp';
import LeftLogoPNG from '@crazyegginc/hatch/dist/images/logo-g2crowd-high-performer.png';
import RightLogoWebP from '@crazyegginc/hatch/dist/images/logo-capterra.webp';
import RightLogoPNG from '@crazyegginc/hatch/dist/images/logo-capterra.png';
import RightPhoto from '@crazyegginc/hatch/dist/images/people-jack-c.png';

const TEXT_LIST = [
  {
    text: 'Track how many visitors complete each step of your conversion flow',
  },
  {
    text: 'Find which steps cause them to drop off, and fix these bottlenecks',
  },
  {
    text: 'Compare the step-by-step conversion rate of each visitor segment',
  },
];

const TextContent = ({ canManageBilling, ownerEmail, isStripeUser }) => {
  return (
    <>
      <UpsellWall.Title
        titleText={
          canManageBilling
            ? 'See what’s stopping visitors from hitting your Conversion Goals'
            : 'Request an upgrade to the Starter plan'
        }
        subtitleText={!canManageBilling ? 'Track how many visitors complete each step of your conversion flow.' : null}
      />
      {canManageBilling ? (
        <UpsellWall.List list={TEXT_LIST} />
      ) : (
        <>
          <p className="mb-6 text-black-pearl-500 text-sm">
            Contact your Account Admin at <strong>{ownerEmail}</strong> to upgrade and access Funnels.
          </p>
        </>
      )}
      <UpsellWall.CTA
        metricType="funnels_upsell_paywall"
        canManageBilling={canManageBilling}
        ownerEmail={ownerEmail}
        isStripeUser={isStripeUser}
      />
    </>
  );
};

const LeftContent = () => {
  return (
    <div className="flex">
      <WebP
        webp={LeftLogoWebP}
        fallback={LeftLogoPNG}
        width="80"
        height="84"
        alt="high performer winter 2024"
        className="mr-5"
      />
      <div className="flex-1">
        <UpsellWall.TestimonialText textClassNames="mb-10">
          We have been able to gain very useful insights to what users are doing when they reach key pages on our
          website, by tracking their behavior we have been able to implement numerous changes to our site which have
          resulted in higher conversions.
        </UpsellWall.TestimonialText>
        <UpsellWall.TestimonialPerson
          name="Nick C."
          title="Director of Business Development and Marketing, Small-Business (50 or fewer employees)"
        />
      </div>
    </div>
  );
};

const RightContent = () => {
  return (
    <div className="flex">
      <WebP webp={RightLogoWebP} fallback={RightLogoPNG} width="80" height="80" alt="capterra" className="mr-5" />
      <div className="flex-1">
        <UpsellWall.TestimonialText textClassNames="mb-10">
          If your website does not convert as it should, this is the software to use. You can take out the guessing from
          the equation using their charts, funnels and recordings of real people using your website. The implementation
          was painless with just a JavaScript code, and the dashboard is very intuitive.
        </UpsellWall.TestimonialText>
        <UpsellWall.TestimonialPerson
          name="Jack C."
          title="IT Manager"
          imagePNG={RightPhoto}
          imageWidth="40"
          imageHeight="40"
        />
      </div>
    </div>
  );
};

export function FunnelsUpsellPaywall() {
  const { currentAccount } = useAuthContext();

  const permissions = usePermissions();
  const canManageBilling = permissions.can('edit', FEATURES.BILLING).allowed;
  const isStripeUser = Boolean(currentAccount?.useStripe);

  return (
    <UpsellWall>
      <UpsellWall.TopPanel
        textContent={
          <TextContent
            canManageBilling={canManageBilling}
            ownerEmail={currentAccount.ownerEmail}
            isStripeUser={isStripeUser}
          />
        }
        imagePNG={FeaturedImagePNG}
        imageWebP={FeaturedImageWebP}
        imageWidth="522"
        imageHeight="256"
        imageAlt="funnels feature"
      />
      <UpsellWall.BottomPanel isSeparate={true} leftContent={<LeftContent />} rightContent={<RightContent />} />
    </UpsellWall>
  );
}
